import { MotifButton, MotifModal } from '@ey-xd/motif-wc-react';
import { useClientStore } from '@/client/client-store';
import { useMfeMountStore } from '@/mfe/mfe-mount-store';
import { navigateToClientScope } from '@/router/client-navigation';

interface Props {
  pendingClientId: string | null;
  /** @param confirmed `true` when the user clicked Yes, `false` on cancel. */
  onClose: (confirmed: boolean) => void;
}

/**
 * Confirmation dialog shown when switching client. Mirrors the legacy
 * `<ey-modal #clientSwitchModal>` block in `app.component.html`.
 *
 * On confirm: commits the new client to the store (which persists IDs to
 * `localStorage`); on cancel: resets to the previously selected client.
 */
export function ClientSwitchModal({ pendingClientId, onClose }: Props) {
  const available = useClientStore((s) => s.available);
  const setCurrent = useClientStore((s) => s.setCurrent);

  const pending = pendingClientId ? available.find((c) => c.id === pendingClientId) : null;
  const isOpen = pending != null;

  const confirm = () => {
    if (pending) {
      // Raise the blocking mount overlay BEFORE committing the new client, so
      // the header dropdown flip is hidden behind the loader until the new
      // client's MFE finishes loading. Without this the store update repaints
      // the dropdown to the new client a frame before the MFE-host effect turns
      // the loader on (PRTL-2907: "client changes, then the loader is visible").
      // `end(appId)` clears it when the same-appId MFE reloads in place.
      const mount = useMfeMountStore.getState();
      mount.begin(mount.appId || 'home');
      setCurrent(pending);
      navigateToClientScope(pending.id);
    }
    onClose(true);
  };

  const cancel = () => {
    onClose(false);
  };

  // Only allow the modal to close via the Yes / No buttons, not by clicking
  // the backdrop or pressing Escape. The Motif modal fires `onModalClose` for
  // all three dismiss paths and includes a `reason` so we can selectively
  // ignore backdrop and keyboard dismissals.
  const handleModalClose = (e: CustomEvent<{ reason: string }>) => {
    if (e.detail?.reason === 'backdropClick' || e.detail?.reason === 'escapeKeyDown') {
      return;
    }
    cancel();
  };

  return (
    <div className="motif-theme-light">
      <MotifModal
        open={isOpen}
        onModalClose={handleModalClose}
        headerTitle="Client Change"
        size="md"
        showFeaturedIcon={false}
        style={
          {
            '--modal-overflow-y': 'hidden',
            '--modal-body-overflow-y': 'hidden',
            '--modal-footer-width': '100%',
          } as React.CSSProperties
        }
      >
        <p slot="body">
          Please make sure to save any changes before changing the client,{' '}
          <b className="eyfontbold">ALL OPEN TABS</b> will be refreshed. Would you like to proceed?
        </p>
        <div slot="footer" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <MotifButton variant="secondary" onClick={cancel}>
              No
            </MotifButton>
            <MotifButton variant="primary" onClick={confirm}>
              Yes
            </MotifButton>
          </div>
        </div>
      </MotifModal>
    </div>
  );
}
